/* storybook-check-ignore */
import { css } from '@emotion/react';
import { BoxProps, Flex, Text } from '@opendoor/bricks/core';
import { OpenHouse } from 'declarations/exclusives/exclusiveListingInfo';
import { Listing } from 'declarations/exclusives/listing';
import { getHasOngoingOrUpcomingOpenHouses } from 'helpers/exclusives/listing';
import moment from 'moment-timezone';
import { useMemo } from 'react';

const OPEN_HOUSE_TIME_FORMAT = 'h:mm A';
const OPEN_HOUSE_DATE_TIME_FORMAT = `dddd, MMM D, ${OPEN_HOUSE_TIME_FORMAT}`;

const CT_TIMEZONE = 'America/Chicago';

export default function OpenHouseBanner({ listing, ...props }: { listing: Listing } & BoxProps) {
  const openHouses = useMemo(
    () => listing.exclusive_listing_info.ongoing_and_upcoming_open_houses || [],
    [listing],
  );
  const timezone = listing.timezone || CT_TIMEZONE;
  const hasOpenHouse = getHasOngoingOrUpcomingOpenHouses(listing);
  return (
    <Flex width="100%" padding="1.25rem 0" flexDirection="column" {...props}>
      <Flex flexDirection="row" justifyContent="space-between">
        <Text mb="0.5rem" fontSize="s2" lineHeight="s1" color="neutrals100" fontWeight="semibold">
          Open House
        </Text>
      </Flex>
      <Text marginTop="0.25rem" marginBottom="0.5rem" fontSize="s1" lineHeight="s1" opacity="0.8">
        {hasOpenHouse ? (
          'This home is only open to tours during these dates'
        ) : (
          <>
            Open House dates and times will be{' '}
            <Text color="brand50" as="span" fontSize="s1" lineHeight="s1" fontWeight="semibold">
              available soon
            </Text>
            . We can send you an email with more information once this home is ready to tour.
          </>
        )}
      </Text>
      <Flex flexDirection="column" gridGap="0.5rem">
        {openHouses.map((openHouse, i) => {
          return (
            <OpenHouseDate key={`openHouseDate${i}`} openHouse={openHouse} timezone={timezone} />
          );
        })}
      </Flex>
    </Flex>
  );
}

const OPEN_HOUSE_MONTH_AND_DAY_FORMAT = 'MMM.DD';

function OpenHouseDate({ openHouse, timezone }: { openHouse: OpenHouse; timezone: string }) {
  const startTime = moment(openHouse.start_time);
  const endTime = moment(openHouse.end_time);
  const [startTimeDay, ...restOfStartTime] = startTime
    .tz(timezone)
    .format(OPEN_HOUSE_DATE_TIME_FORMAT)
    .split(',');
  const [month, day] = startTime.tz(timezone).format(OPEN_HOUSE_MONTH_AND_DAY_FORMAT).split('.');
  return (
    <Flex
      p="11px"
      border="1px solid"
      borderColor="neutrals40"
      borderRadius="roundedSquare"
      alignItems="center"
      gridGap="19px"
      css={css`
        .month {
          font-family: 'Graphik';
          font-weight: 600;
          font-size: 8.5px;
          line-height: 100%;
          fill: var(--colors-neutrals0);
        }
        .day {
          font-family: 'Graphik';
          font-weight: 600;
          font-size: 13.6px;
          line-height: 22px;
          fill: var(--colors-neutrals100);
        }
      `}
    >
      <svg
        width="34"
        height="34"
        viewBox="0 0 34 34"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ borderRadius: '4px' }}
      >
        <rect width="34" height="34" rx="4" fill="#F2F2F2" />
        <rect width="34" height="13.6" fill="#C34143" />
        <text x="9" y="10" className="month">
          {month.toUpperCase()}
        </text>
        <text x="7.75" y="27" className="day">
          {day}
        </text>
      </svg>
      <Text fontSize={['s0', 's0', 's1']} lineHeight={['s00', 's00', 's0']}>
        <Text
          as="span"
          fontWeight="semibold"
          fontSize={['s0', 's0', 's1']}
          lineHeight={['s00', 's00', 's0']}
        >
          {startTimeDay}
        </Text>
        <Text
          fontSize={['s0', 's0', 's1']}
          lineHeight={['s00', 's00', 's0']}
          as="span"
          display={[null, null, null, 'none']}
        >
          <br />
        </Text>
        {restOfStartTime.join(',')} - {endTime.tz(timezone).format(OPEN_HOUSE_TIME_FORMAT)}
      </Text>
    </Flex>
  );
}
